import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Loading } from "../../Components/Loading/Loading";
import {
  useEditProductQuery,
  useUpdateProductMutation,
} from "../../Generated/graphql";
import "./Edit.css";
import { isNull, isUndefined } from "lodash";
import { ProductFields } from "./ProductFields";
import { EditActions } from "./EditActions";
import { StateButton } from "./StateButton";
import { useEditProductOrGroup } from "./useEditProductOrGroup";
import { editNavigationVar } from "../../App";
import { FormElement } from "../../Components/FormElement/FormElement";
import { Select, SelectOption } from "../../Components/Select/Select";

type Props = {
  productId: string;
};

/**
 * Screen to update a product - ie. a Single or a Variant of a Variant Group
 */
export const EditProduct: FC<Props> = ({ productId }) => {
  // State to control the selected product level A
  const [productLevelAId, setProductLevelAId] = useState("");

  // Fetch the current values
  const { loading, error, data, refetch } = useEditProductQuery({
    variables: { productNumber: productId },
    fetchPolicy: "no-cache", // Avoid bug when navigating to new products
  });

  // Set up the mutation needed for saving data
  const [updateProductMutation] = useUpdateProductMutation();

  const {
    customData,
    setCustomData,
    showInShop,
    handleChangeShowInShop,
    category,
    hasCustomDataChanged,
    hasShowInShopChanged,
    isDirty,
  } = useEditProductOrGroup({
    initialCustomData: data?.product?.customData ?? null,
    initialProductData: data?.product?.productData ?? null,
    initialShowInShop: data?.product?.showInShop ?? null,
  });

  const productNumber = useMemo(() => {
    return data?.product?.prodNo ?? null;
  }, [data?.product?.prodNo]);

  const initialProductLevelAId = data?.product?.productLevelA?.id ?? "";
  const hasProductLevelAChanged =
    productLevelAId !== initialProductLevelAId;

  const handleSave = useCallback(async () => {
    // Just a sanity check to avoid saving null data
    if (isNull(customData) || isNull(productNumber)) {
      alert("Kunne ikke gemme produktet. Prøv igen senere.");
      return;
    }

    // Only update the fields that have changed
    const dataToUpdate = {
      ...{ customData: hasCustomDataChanged ? customData : undefined },
      ...{ showInShop: hasShowInShopChanged ? showInShop : undefined },
      productLevelAId: hasProductLevelAChanged
        ? productLevelAId || null
        : undefined,
    };

    // Update the product
    await updateProductMutation({
      variables: { prodNo: productNumber, ...dataToUpdate },
    });

    // Refetch the product
    await refetch();
  }, [
    customData,
    hasCustomDataChanged,
    hasProductLevelAChanged,
    hasShowInShopChanged,
    productNumber,
    refetch,
    showInShop,
    updateProductMutation,
    productLevelAId,
  ]);

  // Set the navigation var to properly highlight the selected product
  useEffect(() => {
    // We need both the category and the product number to set the navigation var
    if (category === null || productNumber === null) {
      return;
    }

    // Set the navigation var
    editNavigationVar({
      selectedCategory: category,
      selectedProductNumber: productNumber,
      selectedName: data?.product?.productData?.name,
    });
  }, [category, data?.product?.productData?.name, productNumber]);

  const productLevelALevels = data?.productLevelALevels ?? [];

  // Set the product level A id when the data is loaded
  useEffect(() => {
    setProductLevelAId(initialProductLevelAId);
  }, [initialProductLevelAId, productId]);

  if (
    loading ||
    error ||
    isUndefined(data) ||
    isUndefined(data.product) ||
    isNull(data.product)
  ) {
    return <Loading />;
  }

  return (
    data.product &&
    data.metadata && (
      <div className="Edit__content-wrapper">
        <div className="Edit__content">
          <div className="Edit__content-inner">
            <FormElement label={"Kategori niveau A"}>
              <Select
                change={(value) => setProductLevelAId(value)}
                value={productLevelAId}
                type="default"
              >
                {productLevelALevels.map(({ id, name }) => (
                  <SelectOption key={id} value={id}>
                    {name}
                  </SelectOption>
                ))}
              </Select>
            </FormElement>
            <ProductFields
              customData={customData ?? {}}
              customDataChanged={(customData) => setCustomData(customData)}
              metadata={data.metadata}
              productData={data.product.productData}
              groupProductType={data.product.type}
            />
            {/* <Properties product={data.product} /> */}
          </div>
        </div>
        <div className="Edit__actions">
          <EditActions
            onSave={() => handleSave()}
            saveState={isDirty || hasProductLevelAChanged ? "dirty" : "clean"}
            lastUpdateUsername={data.product.updated_by ?? undefined}
            lastUpdatedAt={data.product.updated_at ?? undefined}
            otherActions={
              <>
                {showInShop !== null && (
                  <StateButton
                    click={handleChangeShowInShop}
                    state={showInShop}
                  />
                )}
              </>
            }
          />
        </div>
      </div>
    )
  );
};
